import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { CreditCardUpdate } from '../models/credit-card-update.interface';
import { CreditCard } from '../models/credit-card.interface';
import { CreditCardDataService } from '../services/credit_card.service';
// NOTE1: the CreditCardDataService has a store, but is a v2 endpoint.
// this store should eventually switch to using an http service with v3 endpoint
// NOTE2: (PE-6756) currently just used in ProfileModule, full refactor out of scope.
// should eventually replace uses in the BillingModule as well, maybe when v3 endpoint is ready

@Injectable({
  providedIn: 'root'
})
export class CreditCardStore {
  private ccList: BehaviorSubject<CreditCard[]> = new BehaviorSubject([]);

  /**
   * Return credit card list.
   */
  get creditCardList(): Observable<CreditCard[]> {
    return this.ccList.asObservable();
  }

  constructor(private ccHttpService: CreditCardDataService) {
    this.loadCreditCardList();
  }

  /**
   * Load list of credit cards for client account.
   */
  loadCreditCardList(): void {
    // NOTE: fetch on this service is really a fetch all
    this.ccHttpService.fetch().subscribe({
      next: (resp) => {
        this.ccList.next(resp);
      },
      error: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    });
  }

  /**
   * Update credit card record for client account.
   * NOTE: this is not a void type because of v2 form setup,
   * we need any errors to pass back component for form feedback
   */
  updateCard(cc: CreditCard, updateData: Partial<CreditCardUpdate>): Observable<null> {
    return this.ccHttpService.update(cc.id, updateData).pipe(
      switchMap((updatedCc) => {
        // If the credit card is updated to be the default (active === true),
        // refresh list so that "default" label is removed from original default in UI
        // else, update store with response
        if (updatedCc.active) {
          this.loadCreditCardList();
        } else {
          const updatedList = this.ccList.getValue().filter((card) => card.id !== updatedCc.id);
          this.ccList.next(updatedList.concat(updatedCc));
        }

        return of(null);
      })
    );
  }

  /**
   * Remove credit card record from client account.
   * NOTE: this is not a void type because of v2 form setup,
   * we need any errors to pass back component for form feedback
   */
  deleteCard(cc: CreditCard): Observable<null> {
    return this.ccHttpService.destroy(cc.id).pipe(
      switchMap(() => {
        const updatedList = this.ccList.getValue().filter((card) => card.id !== cc.id);
        this.ccList.next(updatedList);

        return of(null);
      })
    );
  }
}
